import { throttle } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useClientRect } from '../../hooks'
import { isSsr } from '../../utils/commonUtils'

const Container = React.memo(
  React.forwardRef((props, ref) => <div ref={ref} {...props} />)
)

const StickyBox = React.memo(({ offsetTop, children, className }) => {
  const [rect, ref] = useClientRect()
  const stickyProp = useMemo(() => {
    if (!isSsr && typeof CSS !== 'undefined' && window.CSS.supports) {
      if (window.CSS.supports('position', 'sticky')) return 'sticky'
      else if (window.CSS.supports('position', '-webkit-sticky'))
        return '-webkit-sticky'
    }
  }, [])
  const [style, setStyle] = useState({})

  const getScrollTop = useCallback(
    () => document.body.scrollTop || document.documentElement.scrollTop,
    []
  )

  const prevScrollTop = useRef(0)

  const handleScroll = useCallback(
    throttle(() => {
      const containerHeight = rect ? rect.height : 0
      const scrollTop = getScrollTop()
      const down = scrollTop > prevScrollTop.current
      const delta = containerHeight - window.innerHeight
      const scrollAmt = scrollTop - prevScrollTop.current
      prevScrollTop.current = scrollTop
      if (containerHeight < window.innerHeight) {
        setStyle({ position: stickyProp, top: offsetTop })
      } else {
        setStyle(
          down
            ? {
                position: stickyProp,
                top:
                  style.top !== offsetTop && style.top !== -delta
                    ? style.top - scrollAmt
                    : -delta
              }
            : {
                position: stickyProp,
                top:
                  style.top >= -delta && style.top < offsetTop
                    ? style.top - scrollAmt
                    : offsetTop
              }
        )
      }
    }, 30),
    [rect.height, style.top]
  )

  const memoizedStyle = useMemo(() => style, [JSON.stringify(style)])

  useEffect(() => {
    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  return (
    <Container ref={ref} className={className} style={memoizedStyle}>
      {children}
    </Container>
  )
})
export default StickyBox
